import React, { useEffect, useState, useCallback } from "react";
import { Table, Badge, Spinner, Alert, Form, Button, Collapse } from "react-bootstrap";
import axios from "axios";
import RefreshIcon from "@mui/icons-material/Refresh";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { Url } from "../url/url";




// ---------------------------------------------
// Service tabs — value must match `serviceCategory` saved by each page
// ---------------------------------------------
const TABS = [
  { value: "all", label: "All" },
  { value: "general", label: "General Services" },
  { value: "mother-wellness", label: "Mother Wellness" },
  { value: "boarding", label: "Boarding" },
  { value: "pet-registration", label: "Pet Registration" },
  { value: "pet-relocation", label: "Pet Relocation" },
  { value: "training", label: "Training" },
  { value: "grooming", label: "Grooming" },
];

const STATUS_VARIANT = {
  pending: "warning",
  confirmed: "success",
  cancelled: "danger",
  completed: "secondary",
};

const STATUS_OPTIONS = ["pending", "confirmed", "cancelled", "completed"];

// ---------------------------------------------
// Human-friendly labels for the `details` object keys.
// Anything not listed here falls back to a title-cased version of the key.
// ---------------------------------------------
const DETAIL_LABELS = {
  dropOffDate: "Drop Off Date",
  pickUpDate: "Pick Up Date",
  feedingInstructions: "Feeding Instructions",
  onMedication: "On Medication",
  medicationDetails: "Medication Details",
  specialBehaviour: "Special Behaviour",
  address: "Address",
  location: "Location",
  petName: "Pet Name",
  species: "Species",
  breed: "Breed",
  municipalCorpCity: "Municipal Corp. City",
  breedCategory: "Breed Category",
  rabiesVaccinationDate: "Rabies Vaccination Date",
  microchipNumber: "Microchip Number",
  aadhaarNumber: "Owner Aadhaar Number",
  hasLicence: "Has Municipal Licence",
  packagePrice: "Package Price",
  packagePeriod: "Package Period",
  relocationType: "Relocation Type",
  originCity: "Origin City",
  destinationCity: "Destination City",
  moveDate: "Expected Move Date",
  travelMode: "Preferred Travel Mode",
  hasCrate: "IATA Crate Available",
  healthCert: "Health Certificate",
  vetCheck: "Recent Vet Check",
  countryName: "Country Name",
  category: "Category",
  doctorName: "Doctor Name",
  doctorSpecialty: "Doctor Specialty",
};

const formatDetailLabel = (key) =>
  DETAIL_LABELS[key] ||
  key.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());

const formatDetailValue = (key, value) => {
  if (value === "" || value === null || value === undefined) return "—";
  if (key === "packagePrice") return `₹${value}`;
  if (key === "onMedication" || key === "hasLicence" || key === "hasCrate" || key === "healthCert" || key === "vetCheck") {
    if (value === "will-arrange") return "Will arrange";
    return value === "yes" ? "Yes" : value === "no" ? "No" : value;
  }
  return String(value);
};

const ServicesEnquiry = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [statusFilter, setStatusFilter] = useState("");
  const [updatingId, setUpdatingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axios.get(`${Url}/api/enquiry/counts`);
      setCounts(res.data?.data || {});
    } catch (error) {
      console.error("Error fetching enquiry counts:", error);
    }
  }, []);

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    setErrorMsg("");
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (activeTab !== "all") params.serviceCategory = activeTab;

      const res = await axios.get(`${Url}/api/enquiry`, { params });
      setEnquiries(res.data?.data || []);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      setErrorMsg(
        error?.response?.data?.message ||
          "Could not load enquiries. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter, activeTab]);

  useEffect(() => {
    fetchEnquiries();
    fetchCounts();
  }, [fetchEnquiries, fetchCounts]);

  const handleStatusChange = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const res = await axios.patch(`${Url}/api/enquiry/${id}`, {
        status: newStatus,
      });
      setEnquiries((prev) =>
        prev.map((e) => (e._id === id ? res.data.data : e))
      );
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Could not update status. Please try again.");
    } finally {
      setUpdatingId(null);
    }
  };

  const handleRefresh = () => {
    fetchEnquiries();
    fetchCounts();
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <section className="py-4">
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .enquiry-header {
          letter-spacing: 0.08em;
          font-size: 0.78rem;
          font-weight: 700;
          color: #3b5bfd;
        }
        .enquiry-tabs {
          display: flex;
          gap: 8px;
          overflow-x: auto;
          padding-bottom: 4px;
        }
        .enquiry-tab-btn {
          border: none;
          background: #eef2fc;
          color: #4b5563;
          font-size: 0.82rem;
          font-weight: 600;
          padding: 8px 16px;
          border-radius: 999px;
          white-space: nowrap;
          transition: background 0.15s, color 0.15s;
        }
        .enquiry-tab-btn:hover {
          background: #dbe3fb;
        }
        .enquiry-tab-btn.active {
          background: #3b5bfd;
          color: #fff;
        }
        .enquiry-tab-btn .badge-count {
          margin-left: 6px;
          font-size: 0.7rem;
          opacity: 0.85;
        }
        .enquiry-table thead th {
          background-color: #eef2fc;
          color: #1f2937;
          font-weight: 600;
          font-size: 0.85rem;
          border-bottom: none;
          white-space: nowrap;
        }
        .enquiry-table tbody td {
          font-size: 0.9rem;
          vertical-align: middle;
        }
        .status-select {
          font-size: 0.8rem;
          padding: 0.25rem 0.5rem;
          width: auto;
        }
        .enquiry-detail-row {
          background-color: #fafbff;
        }
        .enquiry-detail-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: 10px 24px;
          padding: 16px 20px;
        }
        .enquiry-detail-item dt {
          font-size: 0.72rem;
          color: #9aa0ad;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          margin-bottom: 2px;
        }
        .enquiry-detail-item dd {
          font-size: 0.86rem;
          color: #1f2937;
          margin-bottom: 0;
        }
        .expand-btn {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
        .expand-btn:hover {
          color: #3b5bfd;
        }
      `}</style>

      <div className="container">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div>
            <div className="enquiry-header mb-1 d-flex align-items-center gap-2">
              <ListAltIcon style={{ fontSize: 16 }} />
              SERVICE ENQUIRIES
            </div>
            <h3 className="fw-bold mb-0">Booking Requests</h3>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Form.Select
              size="sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ width: 160 }}
            >
              <option value="">All statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </option>
              ))}
            </Form.Select>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="d-flex align-items-center gap-2"
              style={{ borderColor: "#3b5bfd", color: "#3b5bfd" }}
            >
              <RefreshIcon
                style={{
                  fontSize: 16,
                  animation: loading ? "spin 1s linear infinite" : "none",
                }}
              />
              Refresh
            </Button>
          </div>
        </div>

        {/* Service-wise tabs */}
        <div className="enquiry-tabs mb-4">
          {TABS.map((tab) => {
            const count =
              tab.value === "all"
                ? Object.values(counts).reduce((sum, c) => sum + c, 0)
                : counts[tab.value] || 0;
            return (
              <button
                key={tab.value}
                type="button"
                className={`enquiry-tab-btn ${activeTab === tab.value ? "active" : ""}`}
                onClick={() => setActiveTab(tab.value)}
              >
                {tab.label}
                <span className="badge-count">({count})</span>
              </button>
            );
          })}
        </div>

        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: "#3b5bfd" }} />
          </div>
        ) : enquiries.length === 0 ? (
          <div className="text-center text-muted py-5 bg-white rounded-4">
            No enquiries found{statusFilter ? ` for "${statusFilter}"` : ""}.
          </div>
        ) : (
          <div className="bg-white rounded-4 p-3 shadow-sm">
            <div className="table-responsive">
              <Table className="enquiry-table mb-0" hover>
                <thead>
                  <tr>
                    <th style={{ width: 36 }} />
                    <th>Name</th>
                    <th>Service</th>
                    <th>Contact</th>
                    <th>Preferred Date &amp; Time</th>
                    <th>Notes</th>
                    <th>Submitted</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((enquiry) => {
                    const hasDetails =
                      enquiry.details && Object.keys(enquiry.details).length > 0;
                    const isExpanded = expandedId === enquiry._id;

                    return (
                      <React.Fragment key={enquiry._id}>
                        <tr>
                          <td>
                            {hasDetails && (
                              <button
                                type="button"
                                className="expand-btn"
                                onClick={() => toggleExpand(enquiry._id)}
                                title="Show details"
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon style={{ fontSize: 20 }} />
                                ) : (
                                  <ExpandMoreIcon style={{ fontSize: 20 }} />
                                )}
                              </button>
                            )}
                          </td>
                          <td className="fw-semibold">{enquiry.name}</td>
                          <td>{enquiry.serviceTitle || enquiry.service}</td>
                          <td>
                            {enquiry.email && <div>{enquiry.email}</div>}
                            <div className="text-muted small">{enquiry.phone}</div>
                          </td>
                          <td>
                            {enquiry.preferredDate || "—"}
                            {enquiry.preferredTime ? ` at ${enquiry.preferredTime}` : ""}
                          </td>
                          <td
                            className="text-muted"
                            style={{ maxWidth: 220, whiteSpace: "normal" }}
                          >
                            {enquiry.message || "—"}
                          </td>
                          <td className="text-muted small">
                            {enquiry.createdAt
                              ? new Date(enquiry.createdAt).toLocaleString()
                              : "—"}
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <Badge bg={STATUS_VARIANT[enquiry.status] || "secondary"}>
                                {enquiry.status}
                              </Badge>
                              <Form.Select
                                size="sm"
                                className="status-select"
                                value={enquiry.status}
                                disabled={updatingId === enquiry._id}
                                onChange={(e) =>
                                  handleStatusChange(enquiry._id, e.target.value)
                                }
                              >
                                {STATUS_OPTIONS.map((s) => (
                                  <option key={s} value={s}>
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                          </td>
                        </tr>

                        {hasDetails && (
                          <tr className="enquiry-detail-row">
                            <td colSpan={8} className="p-0 border-0">
                              <Collapse in={isExpanded}>
                                <div>
                                  <dl className="enquiry-detail-grid mb-0">
                                    {Object.entries(enquiry.details).map(([key, value]) => (
                                      <div className="enquiry-detail-item" key={key}>
                                        <dt>{formatDetailLabel(key)}</dt>
                                        <dd>{formatDetailValue(key, value)}</dd>
                                      </div>
                                    ))}
                                  </dl>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServicesEnquiry;